/**
 * The audit panel (13-32, give-first): the registry's `audit` slot, on an Internal Audits record (`/record/AUD-<id>`).
 *
 * - **The steps** (`nextAuditVerbs`): change the schedule, start, record completion, approve, and add a follow-up
 *   entry, each shown only when the audit's stage and the person's role allow it (Oversight only). The stage's main
 *   step is open first. An Oversight member who is the audit's own auditor sees "Someone else approves this audit"
 *   in place of the approve form (D-13-32-1, the no-self-approval safeguard).
 * - **Each form** is built by `buildAuditVerb` at "Review changes" (every refusal at its field), confirmed line by
 *   line (`Label: old → new`), then saved through the runtime's Writer (`commitAuditVerb`): synchronous, `If-Match`,
 *   the gate again, owed rows persisted (D-13-L-4, D-13-L-5). A 412 says so and offers to reload.
 * - **A carried audit (AUD-1…11)** says so first: until 1 Dec 2026 only the audit kind, the auditor's independence
 *   and a person the carry-in filled in for Centro can change here (`carriedEditableColumns`).
 * - **Working papers**: "attach them on the SharePoint item" (D-13-L-6: no upload control here).
 *
 * `AuditScheduleForm` (the `/audits` page's "Schedule an audit") is the same form for a new row.
 */

import { useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, Input, Select, TextArea } from '../../ui';
import { useActionRuntime } from '../../../hooks/useActionRuntime';
import {
  AUDIT_ROW_KINDS,
  AUDIT_STATUS,
  AUDIT_VERB_INPUTS,
  CARRY_IN,
  auditChoices,
  auditField,
  carriedDefaults,
} from '../../../config/fieldset/internalAudits';
import type { AuditFieldSpec } from '../../../config/fieldset/internalAudits';
import {
  AUDIT_AUTHORITY_TEXT,
  buildAuditVerb,
  canDoAuditVerb,
  carriedEditableColumns,
  nextAuditVerbs,
} from '../../../lib/auditVerbs';
import type { AuditRow, AuditRowInput, AuditVerb, AuditVerbBuild } from '../../../lib/auditVerbs';
import { formatDay, formatInstant, melbourneDayOf, todayMelbourne } from '../../../lib/fieldValues';
import { NOT_RECORDED } from '../../../lib/verbForm';
import { AuditConflictError, AuditRefusedError } from '../../../services/internalAuditService';
import type { RecordIO, SiblingRow } from '../../../services/recordService';
import type { WriteMode } from '../../../services/commitAdapter';
import type { Actor } from '../../../types/actionRegister';
import type { Refusal } from '../../../types/records';
import styles from '../record.module.css';
import own from './AuditPanel.module.css';

export interface AuditSaved {
  verb: AuditVerb;
  ref: string;
  mode: WriteMode;
  auditPending: number;
}

export interface AuditPanelProps {
  row: AuditRow;
  siblings: readonly SiblingRow[];
  actor: Actor;
  /** The item's SharePoint form (working papers are attached there); `null` on a practice audit. */
  editFormUrl: string | null;
  onSaved: (saved: AuditSaved) => void;
  /** After a 412: read the audit again. */
  onReload: () => void;
  /** The clock (tests and the preview pin it). */
  now?: () => Date;
}

const STEP_WORDS: Readonly<Record<AuditVerb, string>> = {
  schedule: 'Schedule an audit',
  edit: 'Change the schedule',
  start: 'Start the audit',
  complete: 'Record completion',
  approve: 'Approve',
  followUp: 'Add a follow-up entry',
};

const STEP_INTRO: Readonly<Record<AuditVerb, string>> = {
  schedule: 'Name the audit, the area and the auditor, and when it is due. It is saved as Awaiting Completion.',
  edit: 'Change who audits it, when it is due or how it is described. The completion and approval are recorded at their own steps.',
  start: 'Set the audit to In Progress. Optional: you can record completion straight from Awaiting Completion.',
  complete: 'Record the day the audit was completed and link the completed forms and evidence. It then waits for approval.',
  approve: 'Record the score and what it means. Approving stamps today and your name; it is the audit’s last step.',
  followUp: 'Log a follow-up review (the verdict on the actions) or an audit meeting. The audit’s own fields don’t change.',
};

const SAVE_WORDS: Readonly<Record<AuditVerb, string>> = {
  schedule: 'Schedule it',
  edit: 'Save the schedule',
  start: 'Start it',
  complete: 'Save completion',
  approve: 'Approve',
  followUp: 'Add it',
};

const ROW_KIND_WORDS: Readonly<Record<string, string>> = {
  Review: 'Follow-up review (the verdict)',
  AuditMeeting: 'Audit meeting',
};

/** The stage's main step, open first. */
function mainStep(row: AuditRow, offered: readonly AuditVerb[]): AuditVerb | null {
  const status = String(row.fields.AuditStatus ?? '');
  const want: AuditVerb =
    status === AUDIT_STATUS.completed ? 'approve' : status === AUDIT_STATUS.approved ? 'followUp' : offered.includes('complete') ? 'complete' : 'edit';
  return offered.includes(want) ? want : (offered[0] ?? null);
}

/** A stored value in words, for the confirm and the "now on the audit" line. */
function auditValueWords(spec: AuditFieldSpec | undefined, value: unknown): string {
  if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) return NOT_RECORDED;
  if (spec?.kind === 'day-new') {
    const d = formatDay(String(value));
    return d === '—' ? String(value) : d;
  }
  if (spec?.kind === 'number') return `${String(value)}%`;
  return String(value);
}

/** The panel. */
export default function AuditPanel({ row, siblings, actor, editFormUrl, onSaved, onReload, now = () => new Date() }: AuditPanelProps) {
  const at = now();
  const defaults = useMemo(() => carriedDefaults(siblings), [siblings]);
  const ctx = useMemo(() => ({ carriedDefaults: defaults }), [defaults]);
  const offered = nextAuditVerbs(row, actor, at);
  const [chosen, setChosen] = useState<AuditVerb | null | undefined>(undefined);
  const picked = chosen === undefined ? mainStep(row, offered) : chosen;
  const carriedCols = carriedEditableColumns(row, at, ctx);
  const approveGate = canDoAuditVerb('approve', row, actor, at);

  if (offered.length === 0) {
    return (
      <p className={styles.verbNote}>
        {actor.roles.includes('Oversight') ? 'There is nothing to record on this audit now.' : AUDIT_AUTHORITY_TEXT['not-oversight']}
      </p>
    );
  }

  return (
    <div className={styles.verbForm}>
      {carriedCols && <CarriedNotice row={row} columns={carriedCols} />}

      <div className={styles.verbs} role="group" aria-label="Audit steps">
        {offered.map((verb) => (
          <Button
            key={verb}
            type="button"
            variant={picked === verb ? 'primary' : 'secondary'}
            className={picked === verb ? styles.verbPicked : undefined}
            aria-pressed={picked === verb}
            onClick={() => setChosen(picked === verb ? null : verb)}
          >
            {STEP_WORDS[verb]}
          </Button>
        ))}
      </div>

      {picked && (
        <div className={styles.verbBody}>
          <p className={styles.verbIntro}>
            {picked === 'edit' && carriedCols ? 'Record what Centro did not carry, or correct a person the carry-in filled in.' : STEP_INTRO[picked]}
          </p>
          {picked === 'approve' && !approveGate.ok ? (
            <p className={styles.verbNote} role="note">
              {AUDIT_AUTHORITY_TEXT[approveGate.reason]}
            </p>
          ) : picked === 'followUp' ? (
            <FollowUpForm key={`${row.ref}|${row.eTag}|f`} row={row} actor={actor} now={now} onSaved={onSaved} onReload={onReload} />
          ) : (
            <AuditForm
              key={`${row.ref}|${row.eTag}|${picked}`}
              verb={picked}
              row={row}
              actor={actor}
              columns={picked === 'edit' && carriedCols ? carriedCols : AUDIT_VERB_INPUTS[picked]}
              initial={initialValues(picked, row, at)}
              ctx={ctx}
              now={now}
              onSaved={onSaved}
              onReload={onReload}
            />
          )}
        </div>
      )}

      <p className={own.papers}>
        {editFormUrl ? (
          <>
            Working papers:{' '}
            <a href={editFormUrl} target="_blank" rel="noreferrer">
              attach them on the SharePoint item ↗
            </a>
          </>
        ) : (
          'Working papers: a practice audit has no SharePoint item to attach them to.'
        )}
      </p>
    </div>
  );
}

function CarriedNotice({ row, columns }: { row: AuditRow; columns: readonly string[] }) {
  const named = columns.map((c) => auditField(c)?.label ?? c);
  return (
    <div className={own.carried} role="note">
      <p className={own.carriedTitle}>Carried in from Centro</p>
      <p className={own.carriedText}>
        Until {formatDay(CARRY_IN.cutoverDay)} only {listWords(named)} can change here. Everything else is still Centro&apos;s: the 30 Nov
        re-run brings Centro&apos;s values back over any change made before then.
      </p>
      <p className={own.carriedRef}>Centro audit id {String(row.fields.CentroRef ?? '')}</p>
    </div>
  );
}

function listWords(items: readonly string[]): string {
  const q = items.map((s) => `“${s.replace(/\?$/, '')}”`);
  if (q.length <= 1) return q.join('');
  return `${q.slice(0, -1).join(', ')} and ${q[q.length - 1]}`;
}

/** What each form starts from: the record's values for `edit`; Melbourne today for the completion day; blanks otherwise. */
function initialValues(verb: AuditVerb, row: AuditRow | null, at: Date): Record<string, string> {
  const out: Record<string, string> = {};
  for (const c of verb === 'edit' ? [...AUDIT_VERB_INPUTS.edit, ...CARRY_IN.defaultable] : AUDIT_VERB_INPUTS[verb]) {
    const spec = auditField(c);
    const v = row?.fields[c];
    if (verb !== 'edit' || v === null || v === undefined) continue;
    out[c] = spec?.kind === 'day-new' ? (melbourneDayOf(String(v)) ?? '') : String(v);
  }
  if (verb === 'complete') out.CompletedOn = todayMelbourne(at);
  return out;
}

// ---------------------------------------------------------------------------
// The form (schedule, edit, start, complete, approve)
// ---------------------------------------------------------------------------

interface AuditFormProps {
  verb: Exclude<AuditVerb, 'followUp'>;
  row: AuditRow | null;
  actor: Actor;
  columns: readonly string[];
  initial: Record<string, string>;
  ctx?: { carriedDefaults: ReadonlySet<string> };
  now: () => Date;
  onSaved: (saved: AuditSaved) => void;
  onReload: () => void;
}

interface ChangeLine {
  column: string;
  label: string;
  from: string;
  to: string;
}

type Built = Extract<AuditVerbBuild, { ok: true }>;

function changeLines(row: AuditRow | null, patch: Record<string, unknown>): ChangeLine[] {
  return Object.entries(patch).map(([column, to]) => {
    const spec = auditField(column);
    return { column, label: spec?.label ?? column, from: auditValueWords(spec, row?.fields[column]), to: to === null ? 'cleared' : auditValueWords(spec, to) };
  });
}

function AuditForm({ verb, row, actor, columns, initial, ctx, now, onSaved, onReload }: AuditFormProps) {
  const { writer, io } = useActionRuntime();
  const uid = useId();
  const [values, setValues] = useState<Record<string, string>>(initial);
  const [phase, setPhase] = useState<'edit' | 'confirm' | 'saving' | 'saved'>('edit');
  const [built, setBuilt] = useState<Built | null>(null);
  const [refusals, setRefusals] = useState<Refusal[]>([]);
  const [conflict, setConflict] = useState<AuditRow | null | undefined>(undefined);
  const [failure, setFailure] = useState<string | null>(null);
  const [savedInfo, setSavedInfo] = useState<AuditSaved | null>(null);
  const busy = phase === 'saving';
  const ready = io !== null || writer.mode === 'practice';
  const today = todayMelbourne(now());

  const refusalAt = new Map(refusals.filter((r) => r.column).map((r) => [r.column as string, r.message]));
  const idOf = (c: string) => `${uid}-${c}`;

  function edit(column: string, value: string) {
    setValues((v) => ({ ...v, [column]: value }));
    setRefusals((rs) => rs.filter((r) => r.column !== column));
    setBuilt(null);
    if (phase !== 'edit') setPhase('edit');
  }

  function review() {
    setFailure(null);
    const set: Record<string, unknown> = {};
    for (const c of columns) {
      const v = values[c] ?? '';
      if (verb === 'schedule' && v.trim() === '') continue;
      set[c] = v.trim() === '' ? null : v;
    }
    const b = buildAuditVerb(verb, row, actor, { set, auditId: crypto.randomUUID() }, now(), ctx);
    if (!b.ok) {
      setRefusals(b.refusals);
      setBuilt(null);
      return;
    }
    setRefusals([]);
    setBuilt(b);
    setPhase('confirm');
  }

  async function save() {
    if (!built) return;
    setPhase('saving');
    setFailure(null);
    try {
      const result = { patch: built.patch, siblings: built.siblings };
      const r =
        verb === 'schedule'
          ? await writer.scheduleAudit(io as RecordIO, result, actor)
          : await writer.commitAuditVerb(io as RecordIO, (row as AuditRow).ref, (row as AuditRow).eTag, result, actor, { verb, row });
      const ref = 'ref' in r && typeof r.ref === 'string' ? r.ref : (row as AuditRow).ref;
      const info: AuditSaved = { verb, ref, mode: writer.mode, auditPending: r.auditPending.length };
      setSavedInfo(info);
      setPhase('saved');
      onSaved(info);
    } catch (err) {
      setPhase('edit');
      if (err instanceof AuditConflictError) setConflict(err.current);
      else if (err instanceof AuditRefusedError) setRefusals([{ code: err.reason, message: AUDIT_AUTHORITY_TEXT[err.reason] }]);
      else setFailure(err instanceof Error ? err.message : String(err));
    }
  }

  const unplaced = refusals.filter((r) => !r.column || !columns.includes(r.column));

  return (
    <div className={styles.verbForm}>
      {conflict !== undefined && (
        <div className={styles.conflict} role="alert">
          <p className={styles.summaryTitle}>
            {conflict ? 'Someone else changed this audit. Check and save again.' : "This audit can't be found any more. It may have been deleted."}
          </p>
          {conflict && (
            <Button type="button" variant="secondary" size="sm" onClick={onReload}>
              Read the audit again
            </Button>
          )}
        </div>
      )}
      {refusals.length > 0 && (
        <div className={styles.summary} role="alert">
          <p className={styles.summaryTitle}>
            {refusals.length === 1 ? 'One thing to fix before this can be saved:' : `${refusals.length} things to fix before this can be saved:`}
          </p>
          <ul className={styles.summaryList}>
            {refusals.map((r, i) => (
              <li key={`${r.code}-${r.column ?? ''}-${i}`}>{r.message}</li>
            ))}
          </ul>
          {unplaced.length > 0 && unplaced.length < refusals.length && <p className={styles.hint}>The rest are shown beside their fields.</p>}
        </div>
      )}

      {columns.length > 0 && (
        <fieldset className={styles.fields} disabled={busy} inert={busy || undefined}>
          {columns.map((c) => (
            <AuditField
              key={c}
              spec={auditField(c) as AuditFieldSpec}
              id={idOf(c)}
              value={values[c] ?? ''}
              message={refusalAt.get(c)}
              today={today}
              actor={actor}
              onChange={(v) => edit(c, v)}
            />
          ))}
        </fieldset>
      )}

      {verb === 'approve' && <p className={styles.hint}>Set when you save: Approved on (today), Approved by (you), Audit status → Approved.</p>}
      {verb === 'complete' && <p className={styles.hint}>Set when you save: Audit status → Completed.</p>}

      {phase === 'confirm' && built && (
        <div className={styles.confirm} role="status">
          <p className={styles.confirmTitle}>Check before you save</p>
          <ul className={styles.changeList}>
            {changeLines(row, built.patch).map((l) => (
              <li key={l.column}>
                <span className={styles.changeLabel}>{l.label}:</span> {l.from} <span aria-hidden="true">→</span>
                <span className={styles.srOnly}> to </span> <strong>{l.to}</strong>
              </li>
            ))}
          </ul>
          <p className={styles.hint}>
            {built.siblings.length} audit {built.siblings.length === 1 ? 'row' : 'rows'} will be written under your name.
          </p>
        </div>
      )}

      {failure && (
        <p className={styles.failure} role="alert">
          Couldn&apos;t save that: {failure}
        </p>
      )}

      {phase === 'saved' && savedInfo && (
        <p className={styles.saved} role="status">
          <span className={styles.tick} aria-hidden="true">
            ✓
          </span>{' '}
          {savedInfo.auditPending > 0 ? `Saved; ${savedInfo.auditPending} audit ${savedInfo.auditPending === 1 ? 'row' : 'rows'} will be posted.` : 'Saved'}
          {savedInfo.mode === 'practice' && <span className={styles.hint}> Practice mode: nothing was written to SharePoint.</span>}
        </p>
      )}

      <div className={styles.actions}>
        {!ready && <span className={styles.hint}>Connecting to SharePoint…</span>}
        {phase === 'confirm' || phase === 'saving' ? (
          <>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => setPhase('edit')}>
              Change it
            </Button>
            <Button type="button" loading={busy} disabled={!ready} onClick={() => void save()}>
              {SAVE_WORDS[verb]}
            </Button>
          </>
        ) : phase === 'saved' ? null : (
          <Button type="button" disabled={!ready} onClick={review}>
            Review changes
          </Button>
        )}
      </div>
    </div>
  );
}

function AuditField({
  spec,
  id,
  value,
  message,
  today,
  actor,
  onChange,
}: {
  spec: AuditFieldSpec;
  id: string;
  value: string;
  message: string | undefined;
  today: string;
  actor: Actor;
  onChange: (v: string) => void;
}) {
  const refused = message ? styles.inputRefused : undefined;
  const describedBy = message ? `${id}-msg` : undefined;
  let control: ReactNode;
  let hint: string | null = null;
  switch (spec.kind) {
    case 'choice': {
      const live = auditChoices(spec.name);
      control = (
        <Select id={id} value={value} className={refused} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)}>
          <option value="">Not answered</option>
          {value !== '' && !live.includes(value) && <option value={value}>{value} (not a current choice)</option>}
          {live.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      );
      break;
    }
    case 'day-new':
      control = (
        <div className={styles.dateRow}>
          <Input id={id} type="date" value={value} className={`${styles.dateInput} ${refused ?? ''}`} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)} />
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(today)}>
            Today
          </Button>
        </div>
      );
      hint = value ? formatDay(value) : NOT_RECORDED;
      break;
    case 'upn':
      control = (
        <div className={styles.inline}>
          <Input id={id} type="email" value={value} className={refused} aria-describedby={describedBy} placeholder="Their Harmony email address" onChange={(e) => onChange(e.target.value)} />
          <Button type="button" variant="secondary" size="sm" onClick={() => onChange(actor.upn)}>
            Me
          </Button>
        </div>
      );
      break;
    case 'textarea':
      control = <TextArea id={id} rows={4} value={value} className={refused} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)} />;
      hint = `${value.length.toLocaleString('en-AU')} / ${(spec.cap ?? 4000).toLocaleString('en-AU')}`;
      break;
    case 'number':
      control = (
        <Input id={id} type="number" inputMode="numeric" min={0} max={100} step={1} value={value} className={`${own.score} ${refused ?? ''}`} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)} />
      );
      hint = 'A whole number from 0 to 100.';
      break;
    case 'link':
      control = <Input id={id} type="url" value={value} className={refused} aria-describedby={describedBy} placeholder="https://…" onChange={(e) => onChange(e.target.value)} />;
      hint = `The folder or file on SharePoint holding the completed forms and evidence (at most ${spec.cap ?? 255} characters).`;
      break;
    default:
      control = <Input id={id} type="text" value={value} className={refused} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)} />;
  }
  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.label}>
        {spec.label}
      </label>
      {control}
      {hint && <p className={styles.hint}>{hint}</p>}
      {message && (
        <p id={`${id}-msg`} className={styles.refusal}>
          {message}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The follow-up rows (§3.4.1 G-208 / G-212)
// ---------------------------------------------------------------------------

function FollowUpForm({ row, actor, now, onSaved, onReload }: { row: AuditRow; actor: Actor; now: () => Date; onSaved: (s: AuditSaved) => void; onReload: () => void }) {
  const { writer, io } = useActionRuntime();
  const uid = useId();
  const today = todayMelbourne(now());
  const [kind, setKind] = useState('');
  const [text, setText] = useState('');
  const [day, setDay] = useState(today);
  const [time, setTime] = useState('');
  const [refusals, setRefusals] = useState<Refusal[]>([]);
  const [phase, setPhase] = useState<'edit' | 'confirm' | 'saving' | 'saved'>('edit');
  const [built, setBuilt] = useState<Built | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const busy = phase === 'saving';
  const ready = io !== null || writer.mode === 'practice';

  const touch = () => {
    setRefusals([]);
    setBuilt(null);
    if (phase !== 'edit') setPhase('edit');
  };

  function review() {
    const input: AuditRowInput = { logKind: kind, text, ...(day !== today || time ? { at: { day, time } } : {}) };
    const b = buildAuditVerb('followUp', row, actor, { rows: [input], auditId: crypto.randomUUID() }, now());
    if (!b.ok) {
      setRefusals(b.refusals);
      return;
    }
    setBuilt(b);
    setPhase('confirm');
  }

  async function save() {
    if (!built) return;
    setPhase('saving');
    try {
      const r = await writer.commitAuditVerb(io as RecordIO, row.ref, row.eTag, { patch: {}, siblings: built.siblings }, actor, { verb: 'followUp', row });
      setPhase('saved');
      onSaved({ verb: 'followUp', ref: row.ref, mode: writer.mode, auditPending: r.auditPending.length });
    } catch (err) {
      setPhase('edit');
      if (err instanceof AuditConflictError) onReload();
      setFailure(err instanceof Error ? err.message : String(err));
    }
  }

  const message = refusals[0]?.message;
  return (
    <div className={styles.composer}>
      <fieldset className={styles.fields} disabled={busy} inert={busy || undefined}>
        <div className={styles.field}>
          <label htmlFor={`${uid}-kind`} className={styles.label}>
            What kind of entry
          </label>
          <Select
            id={`${uid}-kind`}
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              touch();
            }}
          >
            <option value="">Choose one</option>
            {AUDIT_ROW_KINDS.map((k) => (
              <option key={k} value={k}>
                {ROW_KIND_WORDS[k] ?? k}
              </option>
            ))}
          </Select>
        </div>
        <div className={styles.field}>
          <label htmlFor={`${uid}-text`} className={styles.label}>
            What happened
          </label>
          <TextArea
            id={`${uid}-text`}
            rows={3}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              touch();
            }}
          />
          <p className={styles.hint}>No phone numbers or email addresses.</p>
        </div>
        <div className={styles.field}>
          <label htmlFor={`${uid}-day`} className={styles.label}>
            When it happened
          </label>
          <div className={styles.dateRow}>
            <Input
              id={`${uid}-day`}
              type="date"
              value={day}
              className={styles.dateInput}
              onChange={(e) => {
                setDay(e.target.value);
                touch();
              }}
            />
            <Input
              id={`${uid}-time`}
              type="time"
              value={time}
              className={styles.timeInput}
              aria-label="When it happened: time (optional)"
              onChange={(e) => {
                setTime(e.target.value);
                touch();
              }}
            />
          </div>
          <p className={styles.hint}>{day ? formatDay(day) : NOT_RECORDED}{time ? `, ${time}` : ''} (Melbourne). The time is optional.</p>
        </div>
      </fieldset>
      {message && (
        <p className={styles.refusal} role="alert">
          {message}
        </p>
      )}
      {phase === 'confirm' && built && (
        <div className={styles.confirm} role="status">
          <p className={styles.confirmTitle}>Check before you add it</p>
          <p className={styles.confirmNote}>
            {ROW_KIND_WORDS[kind] ?? kind}, {formatInstant(built.siblings[0]?.EntryTimestamp)}. 1 audit row will be written under your name. The audit&apos;s
            own fields don&apos;t change.
          </p>
        </div>
      )}
      {failure && (
        <p className={styles.failure} role="alert">
          Couldn&apos;t add that: {failure}
        </p>
      )}
      {phase === 'saved' && (
        <p className={styles.saved} role="status">
          <span className={styles.tick} aria-hidden="true">
            ✓
          </span>{' '}
          Added to the audit
          {writer.mode === 'practice' && <span className={styles.hint}> Practice mode: nothing was written to SharePoint.</span>}
        </p>
      )}
      <div className={styles.actions}>
        {phase === 'confirm' || phase === 'saving' ? (
          <>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => setPhase('edit')}>
              Change it
            </Button>
            <Button type="button" loading={busy} disabled={!ready} onClick={() => void save()}>
              {SAVE_WORDS.followUp}
            </Button>
          </>
        ) : phase === 'saved' ? null : (
          <Button type="button" disabled={!ready} onClick={review}>
            Review the entry
          </Button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// "Schedule an audit" (the /audits page)
// ---------------------------------------------------------------------------

export function AuditScheduleForm({ actor, onSaved, now = () => new Date() }: { actor: Actor; onSaved: (s: AuditSaved) => void; now?: () => Date }) {
  return (
    <div className={styles.verbBody}>
      <p className={styles.verbIntro}>{STEP_INTRO.schedule}</p>
      <AuditForm verb="schedule" row={null} actor={actor} columns={AUDIT_VERB_INPUTS.schedule} initial={{}} now={now} onSaved={onSaved} onReload={() => undefined} />
    </div>
  );
}
